// src/components/CustomerDashboard.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  Avatar,
  Divider,
  Paper,
} from "@mui/material";
import {
  Work as JobIcon,
  CheckCircle as CompletedIcon,
  Pending as PendingIcon,
  Receipt as InvoiceIcon,
  AddCircle as AddIcon,
  DirectionsCar as CarIcon,
  Folder as FolderIcon,
} from "@mui/icons-material";
import { db } from "../firebase";
import { collection, query, where, onSnapshot, orderBy, limit } from "firebase/firestore";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";

function CustomerDashboard({ onNavigate }) {
  const { customer } = useCustomerAuth();
  const [stats, setStats] = useState({
    totalJobs: 0,
    activeJobs: 0,
    completedJobs: 0,
    unpaidInvoices: 0,
    unpaidAmount: 0,
  });
  const [recentJobs, setRecentJobs] = useState([]);
  const [unpaidInvoices, setUnpaidInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch customer jobs and calculate stats
  useEffect(() => {
    if (!customer) return;

    setLoading(true);

    // Query jobs by customer name
    const jobsQuery = query(
      collection(db, "jobs"),
      where("clientId", "==", customer.id),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(jobsQuery, (snapshot) => {
      const jobs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Calculate stats
      const totalJobs = jobs.length;
      const activeJobs = jobs.filter(j => 
        ["Pending", "Travelling", "On Site", "In Progress"].includes(j.status)
      ).length;
      const completedJobs = jobs.filter(j => j.status === "Completed").length;

      // Get recent jobs (last 5)
      const recent = jobs.slice(0, 5);

      setStats(prev => ({
        ...prev,
        totalJobs,
        activeJobs,
        completedJobs,
      }));
      setRecentJobs(recent);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [customer]);

  // Fetch invoices - check BOTH clientName and customerName fields
  useEffect(() => {
    if (!customer) return;

    const invoicesQuery = query(
      collection(db, "invoices"),
      where("clientId", "==", customer.id),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(invoicesQuery, (snapshot) => {
      const customerInvoices = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filter unpaid invoices
      const unpaid = customerInvoices.filter(inv => 
        inv.status === "unpaid" || inv.status === "pending"
      );

      // Calculate amounts
      const calculateAmount = (invoice) => {
        // Try invoice.total first
        if (invoice.total && invoice.total > 0) return invoice.total;
        if (invoice.totalAmount && invoice.totalAmount > 0) return invoice.totalAmount;
        
        // Calculate from items array
        if (invoice.items && Array.isArray(invoice.items)) {
          return invoice.items.reduce((sum, item) => {
            const qty = item.quantity || 1;
            const rate = item.rate || item.price || 0;
            return sum + (qty * rate);
          }, 0);
        }
        
        return 0;
      };

      const unpaidAmount = unpaid.reduce((sum, inv) => sum + calculateAmount(inv), 0);

      setStats(prev => ({
        ...prev,
        unpaidInvoices: unpaid.length,
        unpaidAmount,
      }));
      setUnpaidInvoices(unpaid);
    });

    return () => unsubscribe();
  }, [customer]);


  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "success";
      case "In Progress":
      case "On Site":
        return "info";
      case "Travelling":
        return "warning";
      case "Pending":
      default:
        return "default";
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: 1400, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
        <Typography variant="h4" sx={{ color: "white", fontWeight: 700, mb: 1, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
          Welcome back, {customer?.name}! 👋
        </Typography>
        <Typography variant="body1" sx={{ color: "rgba(255, 255, 255, 0.7)", fontSize: { xs: '1rem', sm: '1.15rem' } }}>
          Here's what's happening with your services
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
              color: "white",
            }}
          >
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 48, height: 48 }}>
                  <JobIcon fontSize="medium" />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.3rem', sm: '2rem' } }}>
                    {stats.totalJobs}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Jobs
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              color: "white",
            }}
          >
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 48, height: 48 }}>
                  <PendingIcon fontSize="medium" />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.3rem', sm: '2rem' } }}>
                    {stats.activeJobs}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Active Jobs
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
              color: "white",
            }}
          >
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 48, height: 48 }}>
                  <CompletedIcon fontSize="medium" />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.3rem', sm: '2rem' } }}>
                    {stats.completedJobs}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Completed
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: stats.unpaidInvoices > 0
                ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                : "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)",
              color: "white",
            }}
          >
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 48, height: 48 }}>
                  <InvoiceIcon fontSize="medium" />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.3rem', sm: '2rem' } }}>
                    {stats.unpaidInvoices}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Unpaid Invoices
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Card sx={{ mb: { xs: 2, sm: 3, md: 4 }, bgcolor: "background.paper" }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            Quick Actions
          </Typography>
          <Grid container spacing={{ xs: 1, sm: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<AddIcon />}
                onClick={() => onNavigate("requestJob")}
                sx={{
                  py: 1.2,
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  background: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #d97706 0%, #dc2626 100%)",
                  },
                }}
              >
                Request Job
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<JobIcon />}
                onClick={() => onNavigate("jobs")}
                sx={{ py: 1.2, fontSize: { xs: '0.95rem', sm: '1rem' } }}
              >
                View All Jobs
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<InvoiceIcon />}
                onClick={() => onNavigate("invoices")}
                sx={{ py: 1.2, fontSize: { xs: '0.95rem', sm: '1rem' } }}
              >
                View Invoices
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<CarIcon />}
                onClick={() => onNavigate("vehicles")}
                sx={{ py: 1.2, fontSize: { xs: '0.95rem', sm: '1rem' } }}
              >
                My Vehicles
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={{ xs: 1, sm: 2, md: 3 }}>
        {/* Recent Jobs */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", flexDirection: { xs: 'column', sm: 'row' }, justifyContent: "space-between", alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Recent Jobs
                </Typography>
                <Button size="small" onClick={() => onNavigate("jobs")}> 
                  View All
                </Button>
              </Box>

              {recentJobs.length === 0 ? (
                <Alert severity="info">No jobs yet. Request your first job!</Alert>
              ) : (
                <Stack spacing={1.5}>
                  {recentJobs.map((job) => (
                    <Paper
                      key={job.id}
                      sx={{
                        p: { xs: 1, sm: 2 },
                        border: "1px solid",
                        borderColor: "divider",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        "&:hover": {
                          borderColor: "#f59e0b",
                          transform: "translateX(4px)",
                        },
                      }}
                      onClick={() => onNavigate("jobs")}
                    >
                      <Box sx={{ display: "flex", flexDirection: { xs: 'column', sm: 'row' }, justifyContent: "space-between", alignItems: { xs: 'flex-start', sm: 'center' }, mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {job.jobTemplateName || "Job"}
                        </Typography>
                        <Chip
                          label={job.status}
                          color={getStatusColor(job.status)}
                          size="small"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Vehicle: {job.vehicleRegistration || "N/A"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(job.createdAt)}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Unpaid Invoices */}
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", flexDirection: { xs: 'column', sm: 'row' }, justifyContent: "space-between", alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Unpaid Invoices
                </Typography>
                {stats.unpaidAmount > 0 && (
                  <Chip
                    label={`£${stats.unpaidAmount.toFixed(2)}`}
                    color="error"
                    size="small"
                  />
                )}
              </Box>

              {unpaidInvoices.length === 0 ? (
                <Alert severity="success">All invoices paid! 🎉</Alert>
              ) : (
                <Stack spacing={1.5}>
                  {unpaidInvoices.map((invoice) => (
                    <Paper
                      key={invoice.id}
                      sx={{
                        p: { xs: 1, sm: 2 },
                        border: "1px solid",
                        borderColor: "divider",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        "&:hover": {
                          borderColor: "#ef4444",
                          transform: "translateX(4px)",
                        },
                      }}
                      onClick={() => onNavigate("invoices")}
                    >
                      <Box sx={{ display: "flex", flexDirection: { xs: 'column', sm: 'row' }, justifyContent: "space-between", alignItems: { xs: 'flex-start', sm: 'center' }, mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {invoice.invoiceNumber}
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: "#ef4444", fontWeight: 600 }}>
                          £{invoice.totalAmount?.toFixed(2)}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Due: {formatDate(invoice.dueDate)}
                      </Typography>
                    </Paper>
                  ))}
                  <Button
                    variant="contained"
                    fullWidth
                    color="error"
                    onClick={() => onNavigate("invoices")}
                  >
                    Pay Now
                  </Button>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default CustomerDashboard;